import React, { useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { FLAGS, FLAG_PRIORITY, STATUS } from '../../calendar/mockData';
import type { WeekAppointment } from '../../calendar/types';
import { appointmentFamily, cellTone, TONE_STYLES } from '../../calendar/utils';

const FLAG_TONE_MAP: Record<string, keyof typeof TONE_STYLES> = {
  green: 'green',
  yellow: 'yellow',
  red: 'red',
  ctx: 'ctx',
};

export interface WeekAppointmentCellProps {
  appt: WeekAppointment;
  onClick?: () => void;
}

export function WeekAppointmentCell({ appt, onClick }: WeekAppointmentCellProps) {
  const [hov, setHov] = useState(false);
  const tone = cellTone(appt.flags);
  const tn = TONE_STYLES[tone];
  const st = STATUS[appt.status] ?? { label: 'Scheduled', tone: 'neutral' as const };
  const fam = appointmentFamily(appt.status);
  const sorted = [...appt.flags].sort((a, b) => FLAG_PRIORITY.indexOf(a) - FLAG_PRIORITY.indexOf(b));
  const flagsToShow = sorted.slice(0, 2);
  const more = sorted.length - flagsToShow.length;
  const h12 = appt.hour > 12 ? appt.hour - 12 : appt.hour;
  const period = appt.hour >= 12 ? 'p' : 'a';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter') onClick?.();
      }}
      onMouseEnter={() => { setHov(true); }}
      onMouseLeave={() => { setHov(false); }}
      title={`${String(h12)}${period} · ${appt.patient} · ${appt.procedure}`}
      className={cn(
        'appointment-card relative flex h-full min-h-[60px] cursor-pointer flex-col overflow-hidden rounded-[10px] px-2 py-1.5 pl-[11px] transition-[transform,box-shadow,border-color] duration-[160ms] ease-[var(--ease-out)]',
        hov && '-translate-y-px shadow-[var(--sh-2)] z-[5]',
        !hov && 'z-[1]',
      )}
      style={{
        background: fam.fill,
        border: `1px solid ${hov ? fam.rail : fam.border}`,
      }}
    >
      <span className="absolute bottom-0 left-0 top-0 w-[3px] rounded-l-[10px]" style={{ background: fam.rail }} />
      <div className="flex items-center justify-between gap-1">
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ background: tn.fg, boxShadow: `0 0 0 3px color-mix(in srgb, ${tn.fg} 16%, transparent)` }}
          />
          <span className="font-[var(--font-mono)] text-[10px] font-semibold text-[var(--ink-3)]">
            {h12}
            {period}
          </span>
        </span>
        <span
          className="whitespace-nowrap rounded px-1.5 py-px text-[9px] font-bold uppercase tracking-[0.03em] text-white"
          style={{ background: fam.rail }}
        >
          {st.label}
        </span>
      </div>
      <div className="mt-0.5 min-h-0 flex-1 truncate text-[11.5px] font-semibold leading-tight tracking-[-0.01em] text-[var(--ink-0)]">
        {appt.patient}
      </div>
      <div className="mt-auto flex items-center justify-between gap-1 pt-1">
        <div className="flex items-center gap-[3px]">
          {flagsToShow.map((fk) => {
            const f = FLAGS[fk];
            if (!f) return null;
            const ft = TONE_STYLES[FLAG_TONE_MAP[f.tone] ?? 'ctx'];
            return (
              <span
                key={fk}
                title={f.label}
                className="inline-flex h-[15px] w-[15px] items-center justify-center rounded"
                style={{ background: ft.tint, color: ft.fg }}
              >
                <Icon kind={f.icon} size={9} stroke={2.2} />
              </span>
            );
          })}
          {more > 0 && (
            <span className="font-[var(--font-mono)] text-[9px] font-bold text-[var(--ink-4)]">+{more}</span>
          )}
        </div>
        <span className="font-[var(--font-mono)] text-[10px] font-semibold text-[var(--ink-2)]">
          ${appt.production.toLocaleString()}
        </span>
      </div>
    </div>
  );
}
